import {
  withAllRowsRead,
  withRowsCleared,
  withRowRead,
  withoutRow,
  withoutApproval,
  liveMembers,
} from "./notification-summary";
/**
 * Zustand store for user notifications.
 *
 * Holds the live feed pushed over the WebSocket (sounds, system toasts and
 * the tray icon's attention read it), the backend summary that owns the one
 * notification count (search hub spec §5), and the transient toast queue.
 */

import { decideApproval } from "@/lib/decide-approval";
import { conversationBrowserAccountKey } from "@/hooks/conversation-browser-cache";
import { subscribeDesktopChatAuthScope } from "@/desktop/desktop-chat-auth-scope";
import { create } from "zustand";
import {
  isMutationEmitSuppressed,
  mirroredStore,
  withMutationEmitEnabled,
  withMutationEmitSuppressed,
} from "@/lib/mirrored-store";
import {
  forwardSystemToastAddition,
  listenForSystemToastAdditions,
  listenForSystemToastDismissals,
} from "@/lib/system-toast-dismissal";
import { isTauri } from "@/lib/is-tauri";
import * as notificationsApi from "@/api/notifications";
import {
  EMPTY_NOTIFICATION_SUMMARY,
  type Notification,
  type NotificationSummary,
  type NotificationRowData,
  type PendingApproval,
} from "@/api/notifications";
import { playNotificationSound } from "@/lib/notification-sound";
import { isQuietNotification } from "@/lib/quiet-notifications";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { markdownToPlainText } from "@/lib/markdown-plain-text";
import type { SystemToast, SystemToastInput } from "./notification-types";
export type {
  SystemToast,
  SystemToastIcon,
  SystemToastInput,
} from "./notification-types";

import {
  addToastToState,
  applyForwardedSystemToast,
  installNotificationSystemNoticeGate,
  installSystemToastTextRefresh,
  rememberSystemToastText,
  removeSystemToastFromState,
  resolveSystemToast,
} from "./system-toast-state";
import {
  deferOnboardingSystemNotice,
  isPublishingDeferredSystemNotice,
  onboardingSystemNoticesBlocked,
  systemNoticePresented,
} from "@/lib/onboarding-system-notices";
import { translate } from "@/i18n";
import { storesNoticesMessages } from "@/i18n/messages/stores-notices";
/** Live events arrive in bursts; one summary request covers a burst. */
const SUMMARY_REFRESH_DELAY_MS = 400;
/** setTimeout's largest delay. */
const MAX_TIMER_MS = 2_147_483_647;

/** Server notices with nothing to act on in the canvas become system pills (spec §5.2). */
const SYSTEM_TOAST_NOTIFICATION_TYPES: ReadonlySet<
  Notification["notification_type"]
> = new Set(["info", "capacity_deferred"]);

function isNotificationCenterItem(notification: Notification): boolean {
  // ask_user already pauses the agent and renders an actionable question on
  // the cursor (or its docked compact card). Repeating it in the hub, count,
  // sound, or toast adds noise without adding discoverability.
  return notification.notification_type !== "awaiting_input";
}

export interface NotificationState {
  /** Live feed from the WebSocket: sounds, system toasts and the tray icon read it. */
  notifications: Notification[];
  /** Backend summary: the one count, Needs you and the collapsed rows. */
  summary: NotificationSummary;
  summaryLoading: boolean;
  summaryError: string | null;
  systemToasts: SystemToast[];
  deferredSystemToasts: SystemToast[];
  /** Action callbacks keyed by system toast id. */
  systemToastActions: Map<string, () => void>;

  /** Open-marks-read for a collapsed row: every member, optimistic, then the server's summary. */
  readRow: (row: NotificationRowData) => void;
  /** Clear a collapsed row (dismiss notifications, delete CLI errors). */
  clearRow: (row: NotificationRowData) => void;
  /** Allow or deny a pending approval; it leaves Needs you at once. */
  decideApproval: (approval: PendingApproval, allow: boolean) => Promise<void>;
  /** A task changed: refresh when one of its tool approvals is pending here. */
  noteTaskUpdated: (taskId: string) => void;

  /** Load the summary; the count comes only from here. */
  fetchSummary: () => Promise<void>;
  /** Coalesced summary refresh after live events (owner window on desktop). */
  refreshSummarySoon: () => void;
  /** Push a real-time notification (WebSocket). */
  pushNotification: (notification: Notification) => void;
  /** Retarget saved task notifications and rows without resetting read state. */
  rebindTask: (previousTaskId: string, taskId: string) => void;
  /** Apply notifications the server already marked read (WebSocket); no API call. */
  applyServerRead: (ids: string[]) => void;
  /** Mark every row read; pending approvals stay. */
  markAllRead: () => void;
  /** Clear every row; pending approvals stay. */
  clearAll: () => void;
  /** Show a system-level toast; returns the occurrence revision it assigned. */
  addSystemToast: (
    toast: SystemToastInput,
    onAction?: () => void,
  ) => string | null;
  /** Dismiss a system toast by id. */
  removeSystemToast: (id: string, expectedRevision?: string | null) => boolean;
  /** Get the action callback for a system toast (if any). */
  getSystemToastAction: (id: string) => (() => void) | undefined;
}

let summaryRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let approvalExpiryTimer: ReturnType<typeof setTimeout> | null = null;

/** Refresh when the earliest pending approval expires, so Needs you and the count drop it. */
function scheduleApprovalExpiry(
  summary: NotificationSummary,
  refresh: () => void,
): void {
  if (approvalExpiryTimer) clearTimeout(approvalExpiryTimer);
  approvalExpiryTimer = null;
  const next = Math.min(
    ...summary.approvals.map((approval) =>
      approval.expires_at
        ? Date.parse(approval.expires_at)
        : Number.POSITIVE_INFINITY,
    ),
  );
  if (!Number.isFinite(next)) return;
  approvalExpiryTimer = setTimeout(
    refresh,
    Math.min(Math.max(next - Date.now(), 0) + 1_000, MAX_TIMER_MS),
  );
}

/** The owner publishes toast occurrences separately from the shared WS frame. */
function isMirroredToastReplay(): boolean {
  return (
    isTauri() && getWindowRoleSync() === "mirror" && isMutationEmitSuppressed()
  );
}

export const useNotificationStore = create<NotificationState>()(
  mirroredStore("notification", (set, get) => {
    let summaryRevision = 0;
    const guardedSummary = () => {
      const revision = ++summaryRevision;
      const scope = conversationBrowserAccountKey();
      const desktopGuard = captureDesktopChatAuthScopeGuard();
      return (summary: NotificationSummary) => {
        if (
          revision !== summaryRevision ||
          scope !== conversationBrowserAccountKey() ||
          !desktopGuard()
        )
          return false;
        applySummary(summary);
        return true;
      };
    };
    const applySummary = (summary: NotificationSummary) => {
      set({ summary, summaryLoading: false, summaryError: null });
      scheduleApprovalExpiry(summary, () => void get().fetchSummary());
    };
    return {
      rebindTask: (previousTaskId, taskId) =>
        set((state) => {
          const rebind = (notification: Notification) =>
            notification.source_type === "task" &&
            notification.source_id === previousTaskId
              ? { ...notification, source_id: taskId }
              : notification;
          return {
            notifications: state.notifications.map(rebind),
            summary: {
              ...state.summary,
              rows: state.summary.rows.map((row) =>
                row.target?.type === "task" && row.target.id === previousTaskId
                  ? { ...row, target: { ...row.target, id: taskId } }
                  : row,
              ),
              approvals: state.summary.approvals.map((approval) =>
                approval.task_id === previousTaskId
                  ? { ...approval, task_id: taskId }
                  : approval,
              ),
            },
          };
        }),
      notifications: [],
      summary: EMPTY_NOTIFICATION_SUMMARY,
      summaryLoading: false,
      summaryError: null,
      systemToasts: [],
      deferredSystemToasts: [],
      systemToastActions: new Map(),

      fetchSummary: async () => {
        const apply = guardedSummary();
        set({ summaryLoading: true, summaryError: null });
        try {
          const summary = await notificationsApi.getNotificationSummary();
          apply(summary);
        } catch {
          if (apply(get().summary))
            set({
              summaryError: translate(
                storesNoticesMessages,
                "notificationsLoadFailed",
              ),
            });
        }
      },

      refreshSummarySoon: () => {
        // Mirror windows receive the owner's summary through the mirrored store.
        if (isTauri() && getWindowRoleSync() !== "owner") return;
        if (summaryRefreshTimer) clearTimeout(summaryRefreshTimer);
        summaryRefreshTimer = setTimeout(() => {
          summaryRefreshTimer = null;
          void get().fetchSummary();
        }, SUMMARY_REFRESH_DELAY_MS);
      },

      pushNotification: (notification) => {
        if (!isNotificationCenterItem(notification)) return;

        // Dedup against REST+WS race: if the notification was already
        // loaded and a WS event arrives for the same id, don't double-insert
        // (and don't double-sound or double-toast).
        if (get().notifications.some((n) => n.id === notification.id)) return;

        set((state) => ({
          notifications: [notification, ...state.notifications],
        }));
        get().refreshSummarySoon();

        if (
          SYSTEM_TOAST_NOTIFICATION_TYPES.has(notification.notification_type)
        ) {
          get().addSystemToast({
            id: `notification-${notification.id}`,
            title: markdownToPlainText(notification.title),
            description:
              markdownToPlainText(notification.description) || undefined,
            level: "info",
            task:
              notification.source_type === "task" && notification.project_id
                ? {
                    projectId: notification.project_id,
                    taskId: notification.source_id,
                  }
                : undefined,
          });
        }

        // During desktop onboarding the choreographed demo owns the screen:
        // notifications still land in the feed and the summary, but toasts and
        // sounds stay silent until onboarding reaches a terminal step.
        if (onboardingSystemNoticesBlocked()) return;

        // Mirrors apply the same frame, so only the owner window makes noise, and
        // comments and assignments stay silent (spec D11).
        if (
          getWindowRoleSync() === "owner" &&
          !isQuietNotification(notification)
        ) {
          playNotificationSound(notification);
        }
      },

      applyServerRead: (ids) => {
        const readIds = new Set(ids);
        set((state) => ({
          notifications: state.notifications.map((n) =>
            readIds.has(n.id) ? { ...n, read: true } : n,
          ),
        }));
        get().refreshSummarySoon();
      },

      readRow: (row) => {
        const apply = guardedSummary();
        if (row.read) return;
        const members = liveMembers(row);
        set((state) => ({
          notifications: state.notifications.map((n) =>
            members.has(n.id) ? { ...n, read: true } : n,
          ),
          summary: withRowRead(state.summary, row.id),
        }));
        notificationsApi
          .markRowsRead([row])
          .then(apply)
          .catch(() => void get().fetchSummary());
      },

      clearRow: (row) => {
        const apply = guardedSummary();
        const members = liveMembers(row);
        set((state) => ({
          notifications: state.notifications.filter((n) => !members.has(n.id)),
          summary: withoutRow(state.summary, row.id),
        }));
        notificationsApi
          .clearRows([row])
          .then(apply)
          .catch(() => void get().fetchSummary());
      },

      decideApproval: async (approval, allow) => {
        ++summaryRevision;
        const scope = conversationBrowserAccountKey();
        const guard = captureDesktopChatAuthScopeGuard();
        set((state) => ({
          summary: withoutApproval(state.summary, approval.id),
        }));
        try {
          await decideApproval(
            {
              projectId: approval.project_id,
              taskId: approval.task_id,
              approvalId: approval.id,
            },
            allow ? "approve" : "reject",
          );
        } catch {
          // apiRequest's error middleware already told the user; the refresh below restores the row.
        }
        if (scope === conversationBrowserAccountKey() && guard())
          await get().fetchSummary();
      },

      noteTaskUpdated: (taskId) => {
        if (
          get().summary.approvals.some(
            (approval) => approval.task_id === taskId,
          )
        )
          get().refreshSummarySoon();
      },

      markAllRead: () => {
        const apply = guardedSummary();
        set((state) => ({
          notifications: state.notifications.map((n) => ({
            ...n,
            read: true,
          })),
          summary: withAllRowsRead(state.summary),
        }));
        notificationsApi
          .markAllRead()
          .then(apply)
          .catch(() => void get().fetchSummary());
      },

      clearAll: () => {
        const apply = guardedSummary();
        set((state) => ({
          notifications: [],
          summary: withRowsCleared(state.summary),
        }));
        notificationsApi
          .clearAllNotifications()
          .then(apply)
          .catch(() => void get().fetchSummary());
      },

      addSystemToast: (input, onAction?) => {
        // The onboarding flow owns the desktop surface until it reaches a
        // terminal step. System toasts (including trial and CLI-update notices)
        // must follow the same quiet period as server notifications.
        if (isMirroredToastReplay()) return null;
        const { toast, build } = resolveSystemToast(input);
        // A released deferred notice is the same occurrence; anything else is new.
        const revision =
          (isPublishingDeferredSystemNotice() && toast.revision) ||
          crypto.randomUUID();
        const replacement = { ...toast, revision };
        rememberSystemToastText(replacement, build);
        const previousRevision =
          [...get().deferredSystemToasts, ...get().systemToasts].find(
            (item) => item.id === toast.id,
          )?.revision ?? null;
        if (deferOnboardingSystemNotice(replacement, onAction)) {
          forwardSystemToastAddition(replacement, previousRevision);
          return revision;
        }
        const add = () =>
          set((state) => addToastToState(state, replacement, onAction));
        if (forwardSystemToastAddition(replacement, previousRevision))
          withMutationEmitSuppressed(add);
        else withMutationEmitEnabled(add);
        systemNoticePresented(replacement);
        return revision;
      },

      removeSystemToast: (id, expectedRevision) => {
        if (isMirroredToastReplay()) return false;
        return removeSystemToastFromState(
          { getState: get, setState: set },
          id,
          expectedRevision,
        );
      },

      getSystemToastAction: (id) => get().systemToastActions.get(id),
    };
  }),
);

listenForSystemToastDismissals((id, revision) => {
  if (!useNotificationStore.getState().removeSystemToast(id, revision)) {
    useNotificationStore.setState({
      systemToasts: [...useNotificationStore.getState().systemToasts],
    });
  }
});

listenForSystemToastAdditions((toast, expectedRevision) =>
  applyForwardedSystemToast(useNotificationStore, toast, expectedRevision),
);

/** Apply wire state while preserving callbacks only for unchanged occurrences. */
export function applyNotificationPatch(
  patch: Partial<NotificationState>,
): void {
  const next = { ...patch };
  delete next.systemToastActions;
  if (next.systemToasts) {
    const current = useNotificationStore.getState();
    const incoming = new Map(
      next.systemToasts.map((toast) => [toast.id, toast]),
    );
    next.systemToastActions = new Map(
      [...current.systemToastActions].filter(([id]) => {
        const previous = current.systemToasts.find((toast) => toast.id === id);
        const replacement = incoming.get(id);
        return (
          previous && replacement && previous.revision === replacement.revision
        );
      }),
    );
  }
  useNotificationStore.setState(next);
}

/** Drop account-owned data and pending refreshes synchronously at an auth boundary. */
subscribeDesktopChatAuthScope(() => {
  if (summaryRefreshTimer) clearTimeout(summaryRefreshTimer);
  if (approvalExpiryTimer) clearTimeout(approvalExpiryTimer);
  summaryRefreshTimer = approvalExpiryTimer = null;
  useNotificationStore.setState({
    notifications: [],
    summary: EMPTY_NOTIFICATION_SUMMARY,
    summaryLoading: false,
    summaryError: null,
  });
});

installNotificationSystemNoticeGate(useNotificationStore);
installSystemToastTextRefresh(useNotificationStore);
